import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';
import { GoogleMap, MapMarker } from '@angular/google-maps';

/**
 * consts
 */
import { UnitsTranslateMapper } from '../../consts';

/**
 * models
 */
import { IUnitBaseModel, IUnitModel } from '../../models';

/**
 * shared
 */
import { FallbackComponent } from '@sharedComponents/fallback';
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';
import { ListGroupDirective } from '@shared/directives/list-groupe/list-group.directive';
import { ListGroupItemDirective } from '@shared/directives/list-groupe/list-group-item.directive';
import { SpinKitWaveComponent } from '@sharedComponents/spinner/spinkit/components/spin-kit-wave/spin-kit-wave.component';

/**
 * Read-only unit details list and map — reused on the Units page and Control Board modal.
 */
@Component({
    selector: 'app-unit-details',
    templateUrl: './unit-details.component.html',
    styleUrl: './unit-details.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DatePipe,
        /**
         * libs
         */
        TranslatePipe,
        GoogleMap,
        MapMarker,
        /**
         * shared
         */
        FallbackComponent,
        TemplateDirective,
        ListGroupDirective,
        SpinKitWaveComponent,
        ListGroupItemDirective,
    ],
})
export class UnitDetailsComponent {

    /** Translation keys for the details list labels. */
    public readonly TRANSLATE_KEY_MAP = UnitsTranslateMapper;

    /** When true, shows a loading spinner instead of details content. */
    public readonly loading = input<boolean>(false);

    /** Unit to display; null shows the empty placeholder when enabled. */
    public readonly unit = input<IUnitModel | null>(null);

    /** When true and {@link unit} is null, shows the “select an item” placeholder. */
    public readonly showEmptyPlaceholder = input<boolean>(true);

    /** Shared Google Maps display options for the view-only map. */
    public readonly mapOptions: google.maps.MapOptions = {
        fullscreenControl: true,
        zoomControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        rotateControl: false,
        scaleControl: false,
        draggable: true,
    };

    /** Map center and marker position derived from the current {@link unit}. */
    public readonly mapPosition = computed(() => {
        const current = this.unit();
        return current ? this._getLatLng(current) : undefined;
    });

    /**
     * Parses latitude / longitude strings and returns a `LatLngLiteral`,
     * or `undefined` if either value is not a number.
     *
     * @param unit - Unit model carrying lat/lng strings.
     */
    private _getLatLng(unit: IUnitBaseModel): google.maps.LatLngLiteral | undefined {
        const lat = parseFloat(unit.latitude);
        const lng = parseFloat(unit.longitude);

        if (isNaN(lat) || isNaN(lng)) {
            return undefined;
        }

        return { lat, lng };
    }

}
